import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import React from 'react';

import {getNonBatchContainerContents} from 'constants/SampleConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  assertAllAccessible,
  getAccessibleOrInaccessibleId,
  isInaccessible,
} from 'graphql-client/InaccessibleInfo';
import WarningBanner from 'ui-library/banners/WarningBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import ReorderableSimpleTable, {
  reorderableSimpleTableStyles,
} from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import * as DateUtil from 'util/DateUtil';

import {ListContainers, ListContainersVariables} from './__generated/ListContainers';

type ContainerContent = ExtractByTypename<
  ListContainers['containers'][number],
  'ContainerNode'
>['contents'][number];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

export interface ContainerWorklistItemsProps {
  itemIds: Array<string>;
  onReorderRow: (id: string, newIndex: number) => void;
}

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export const LIST_CONTAINERS_QUERY = gql`
  query ListContainers($containerIds: [StrictString]!) @skipAudit @engComponent(name: WORKLISTS) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        name
        modifiedAt
        barcode {
          value
        }
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ContainerWorklistItems(props: ContainerWorklistItemsProps): JSX.Element {
  function getContainerRows(containers: Array<ListContainers['containers'][number]>): Array<Row> {
    return assertAllAccessible(containers).map((container) => ({
      id: container.id,
      cells: [
        {key: `${container.id}:barcode`, contents: <div>{container.barcode?.value}</div>},
        {
          key: `${container.id}:name`,
          contents: <div>{container.name}</div>,
          contentsClassName: css(reorderableSimpleTableStyles.wrapText),
          cellClassName: css(reorderableSimpleTableStyles.wideColumn),
        },
        {
          key: `${container.id}:contents`,
          contents: (
            <ChipList
              itemIds={getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(
                container.contents
              ).map(({containedItem}: {containedItem: ContainedItem}) =>
                getAccessibleOrInaccessibleId(containedItem)
              )}
              chipSize={ChipSize.MINI}
              maxChips={1}
            />
          ),
        },
        {
          key: `${container.id}:modifiedAt`,
          contents: <div>{DateUtil.readableTime(container.modifiedAt, {showTime: false})}</div>,
        },
      ],
    }));
  }

  return (
    <BenchlingQuery<ListContainers, ListContainersVariables>
      query={LIST_CONTAINERS_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{containerIds: props.itemIds}}
    >
      {(data) =>
        data.containers.some((container) => isInaccessible(container)) ? (
          <WarningBanner>
            <span>
              You cannot change the order of this worklist because it contains items that are inaccessible to
              you.
            </span>
          </WarningBanner>
        ) : (
          <ReorderableSimpleTable
            headerCells={[
              {key: 'barcode', contents: <div>Barcode</div>},
              {
                key: 'name',
                contents: <div>Name</div>,
                cellClassName: css(reorderableSimpleTableStyles.wideColumn),
              },
              {key: 'contents', contents: <div>Contents</div>},
              {key: 'modifiedAt', contents: <div>Modified</div>},
            ]}
            isDraggingDisabled={false}
            noRowsDisplay="Worklist is empty"
            onReorderRow={props.onReorderRow}
            rows={getContainerRows(data.containers)}
          />
        )
      }
    </BenchlingQuery>
  );
}
